import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { attendanceService } from "../../../services/api";

/**
 * Read-only cut-off impact (new-joiner hold + deferred LOP).
 * Compact sub-tabs — display only, no value edits.
 */
export default function CutoffImpactPanel({
  payMonth,
  darkMode = false,
  className = "",
}) {
  const [loading, setLoading] = useState(false);
  const [subTab, setSubTab] = useState("joiner");
  const [data, setData] = useState({ joinerHeld: [], deferredLop: [] });
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const ym = String(payMonth || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(ym)) {
      setData({ joinerHeld: [], deferredLop: [] });
      setLoadError("");
      return undefined;
    }
    setLoading(true);
    setLoadError("");
    attendanceService
      .getCutoffImpact(ym)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data || res || {};
        const joinerHeld = Array.isArray(d.joinerHeld) ? d.joinerHeld : [];
        const deferredLop = Array.isArray(d.deferredLop) ? d.deferredLop : [];
        setData({ joinerHeld, deferredLop });
        // Prefer the tab that actually has rows (avoids empty "New joiner hold" when only deferred).
        if (joinerHeld.length === 0 && deferredLop.length > 0) setSubTab("deferred");
        else if (joinerHeld.length > 0) setSubTab("joiner");
      })
      .catch((err) => {
        if (cancelled) return;
        setData({ joinerHeld: [], deferredLop: [] });
        setLoadError(err?.response?.data?.message || err?.message || "Could not load cut-off impact");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [payMonth]);

  const rows = useMemo(
    () => (subTab === "joiner" ? data.joinerHeld : data.deferredLop),
    [subTab, data]
  );

  const border = "border-[hsl(var(--ds-border))]";
  const muted = "text-[hsl(var(--ds-muted-fg))]";

  return (
    <div className={`flex min-h-0 flex-col gap-2 ${className}`}>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        <span className={`text-[10px] font-semibold uppercase tracking-wide ${muted}`}>
          Attendance cut-off
        </span>
        {[
          { id: "joiner", label: "New joiner hold", count: data.joinerHeld.length },
          { id: "deferred", label: "Deferred LOP", count: data.deferredLop.length },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setSubTab(t.id)}
            className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
              subTab === t.id
                ? "border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)]"
                : darkMode
                  ? `border-[hsl(var(--ds-border))] ${muted}`
                  : `border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]`
            }`}
          >
            {t.label} ({t.count})
          </button>
        ))}
        {loading ? <Loader2 size={12} className={`animate-spin ${muted}`} /> : null}
      </div>
      {loadError ? (
        <p className="shrink-0 text-[11px] text-[hsl(var(--ds-danger))]">{loadError}</p>
      ) : null}
      <div className={`max-h-48 min-h-0 overflow-auto rounded-lg border ${border}`}>
        <table className="w-full text-xs">
          <thead
            className={`sticky top-0 z-[1] ${
              darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"
            }`}
          >
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold">Sl. No</th>
              <th className="px-2 py-1.5 text-left font-semibold">Code</th>
              <th className="px-2 py-1.5 text-left font-semibold">Name</th>
              <th className="px-2 py-1.5 text-right font-semibold">Days</th>
              <th className="px-2 py-1.5 text-left font-semibold">Dates</th>
              <th className="px-2 py-1.5 text-left font-semibold">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className={`px-3 py-6 text-center ${muted}`}>
                  {loading
                    ? "Loading…"
                    : subTab === "joiner"
                      ? "No post-cut-off new joiner holds for this month."
                      : "No deferred post-cut-off LOP for this month."}
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.employeeId}-${r.type}-${i}`} className={`border-t ${border}`}>
                  <td className={`px-2 py-1.5 tabular-nums ${muted}`}>{i + 1}</td>
                  <td className="px-2 py-1.5 font-medium">{r.empCode}</td>
                  <td className="px-2 py-1.5">{r.name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-[hsl(var(--ds-danger))]">
                    {r.days}
                  </td>
                  <td className={`max-w-[10rem] truncate px-2 py-1.5 ${muted}`} title={(r.dates || []).join(", ")}>
                    {(r.dates || []).join(", ") || "—"}
                  </td>
                  <td className={`max-w-[18rem] px-2 py-1.5 ${muted}`} title={r.remarks}>
                    <span className="line-clamp-2">{r.remarks || "—"}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
